import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AuthActions, UserActions } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import SmsCode from '@portal/components/SmsCode/SmsCode';

import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MessageOutlined,
  RightOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import SecurityIcon from '@portal/assets/svg/ic_new_security.svg?react';
import Loading from '@portal/components/Loading/Loading';
import PasswordCheck from '@portal/components/PasswordCheck/PasswordCheck';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { passwordRules } from '@portal/config/app.json';
import { TokenMethod } from '@portal/enum/device';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { UserCredentialType } from '@portal/models/enumerators/auth';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';
import { validatePassword } from '@portal/utils/validate';

const ChangePassword = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const {
    user: {
      me: { mustChangePassword, mustChangePin },
    },
    auth: { changeUserCredentialFail, changeUserCredential },
    loading: { amount },
    theme: { currentTheme },
    device: { lastNumbers },
  } = useReduxState();

  const [disableButton, setDisableButton] = useState(true);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(
    validatePassword(password)
  );
  const [
    isChangeCredentialMessageModalVisible,
    setIsChangeCredentialMessageModalVisible,
  ] = useState(false);
  const [isChannelSelected, setIsChannelSelected] = useState(false);
  const [isSmsFocused, setIsSmsFocused] = useState(true);
  const [method, setMethod] = useState<TokenMethod>(TokenMethod.SMS);

  const handleChangePassword = (value: string) => {
    const validate = validatePassword(value);

    setPasswordValidation(validate);
    setPassword(value);
  };

  const checkPasswordValidation = useCallback(
    () => Object.values(passwordValidation).every((field) => field),
    [passwordValidation]
  );

  useEffect(() => {
    if (isChannelSelected) {
      dispatch(AuthActions.userCredentialUpdateToken(method));
    }
  }, [dispatch, isChannelSelected, method]);

  useEffect(() => {
    if (changeUserCredentialFail) {
      MessageService.error(changeUserCredentialFail.message, {
        originalError: changeUserCredentialFail,
      });

      setIsSmsFocused(true);
      dispatch(AuthActions.userCredentialError(null));
    }
  }, [changeUserCredentialFail, dispatch]);

  useEffect(() => {
    if (changeUserCredential) {
      if (mustChangePin) {
        return navigate('/trocar-credenciais/pin');
      } else {
        dispatch(UserActions.getMeFirstLoad());
      }

      dispatch(AuthActions.userCredentialError(null));
      dispatch(AuthActions.userCredentialSuccess(false));
      navigate('/inicio');
    }
  }, [changeUserCredential, dispatch, navigate, mustChangePin]);

  useEffect(() => {
    if (mustChangePassword) {
      setIsChangeCredentialMessageModalVisible(true);
      setIsChannelSelected(false);
      setIsSmsFocused(true);
    }
  }, [navigate, mustChangePassword]);

  useEffect(() => {
    if (!isSmsFocused) {
      setDisableButton(!checkPasswordValidation());
    } else {
      setDisableButton(code.length < 6);

      setPassword('');
    }
  }, [code.length, isSmsFocused, password, checkPasswordValidation]);

  const handleSelectChannel = (selectedMethod: TokenMethod) => {
    setMethod(selectedMethod);
    setIsChannelSelected(true);
  };

  const handleResend = (selectedMethod?: TokenMethod) => {
    const methodToUse = selectedMethod || method;
    setMethod(methodToUse);
    dispatch(AuthActions.userCredentialUpdateToken(methodToUse));
  };

  const handleSubmit = () => {
    if (isSmsFocused) {
      setIsSmsFocused(false);
    } else {
      if (checkPasswordValidation()) {
        dispatch(
          AuthActions.userCredentialChangeCredential({
            type: UserCredentialType.PASSWORD,
            credential: password,
            token: code,
          })
        );
      }
    }
  };

  const handleLogout = () => {
    if (!isChannelSelected) {
      dispatch(AuthActions.logout());
    } else if (isSmsFocused) {
      setIsChannelSelected(false);
      setCode('');
    } else {
      setIsSmsFocused(true);
    }
  };

  return (
    <>
      {amount > 0 && <Loading />}
      <div
        className="change-credentials"
        style={{ background: currentTheme.background }}
      >
        <div className="change-credentials__form">
          <div className="change-credentials__form__icon-security">
            <div className="change-credentials__form__icon-security__circle">
              <LockOutlined />
            </div>
          </div>
          <ThemedText>
            {translate(
              !isChannelSelected
                ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CHANNEL.TITLE'
                : `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.${
                    isSmsFocused ? 'CODE' : 'PASSWORD'
                  }.${
                    isSmsFocused
                      ? `TITLE_${
                          method === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
                        }`
                      : 'TITLE'
                  }`
            )}
          </ThemedText>
          <ThemedText
            variant="body"
            className="change-credentials__form__subtitle"
          >
            {translate(
              !isChannelSelected
                ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CHANNEL.TEXT'
                : `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.${
                    isSmsFocused ? 'CODE' : 'PASSWORD'
                  }.${
                    isSmsFocused
                      ? `TEXT_${
                          method === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
                        }`
                      : 'TEXT'
                  }`
            )}
          </ThemedText>
          <div className="change-credentials__form__secure-badge">
            <SecurityIcon
              fill={currentTheme.text}
              className="change-credentials__form__secure-badge__icon"
            />
            <span>
              {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SECURE')}
            </span>
          </div>
          <div className="change-credentials__form__step-indicator">
            <div
              className={`change-credentials__form__step-indicator__dot${
                !isChannelSelected
                  ? ' change-credentials__form__step-indicator__dot--active'
                  : ''
              }`}
            />
            <div
              className={`change-credentials__form__step-indicator__dot${
                isChannelSelected && isSmsFocused
                  ? ' change-credentials__form__step-indicator__dot--active'
                  : ''
              }`}
            />
            <div
              className={`change-credentials__form__step-indicator__dot${
                isChannelSelected && !isSmsFocused
                  ? ' change-credentials__form__step-indicator__dot--active'
                  : ''
              }`}
            />
          </div>
          {!isChannelSelected ? (
            <div
              className="change-credentials__form__channel-selection"
              key="step-channel"
            >
              <div className="change-credentials__form__channel-selection__options">
                <div
                  className="change-credentials__form__channel-selection__option-card"
                  onClick={() => handleSelectChannel(TokenMethod.SMS)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectChannel(TokenMethod.SMS);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
                  )}
                >
                  <div className="change-credentials__form__channel-selection__option-card__icon change-credentials__form__channel-selection__option-card__icon--sms">
                    <MessageOutlined />
                  </div>
                  <div className="change-credentials__form__channel-selection__option-card__content">
                    <div className="change-credentials__form__channel-selection__option-card__title">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
                      )}
                    </div>
                    <div className="change-credentials__form__channel-selection__option-card__description">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE',
                        {
                          lastNumbers: lastNumbers || '',
                        }
                      )}
                    </div>
                  </div>
                  <RightOutlined className="change-credentials__form__channel-selection__option-card__arrow" />
                </div>
                <div
                  className="change-credentials__form__channel-selection__option-card"
                  onClick={() => handleSelectChannel(TokenMethod.WHATSAPP)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectChannel(TokenMethod.WHATSAPP);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                  )}
                >
                  <div className="change-credentials__form__channel-selection__option-card__icon change-credentials__form__channel-selection__option-card__icon--whatsapp">
                    <WhatsAppOutlined />
                  </div>
                  <div className="change-credentials__form__channel-selection__option-card__content">
                    <div className="change-credentials__form__channel-selection__option-card__title">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                      )}
                    </div>
                    <div className="change-credentials__form__channel-selection__option-card__description">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE',
                        {
                          lastNumbers: lastNumbers || '',
                        }
                      )}
                    </div>
                  </div>
                  <RightOutlined className="change-credentials__form__channel-selection__option-card__arrow" />
                </div>
              </div>
            </div>
          ) : isSmsFocused ? (
            <div className="change-credentials__form__step" key="step-code">
              <SmsCode
                code={code}
                setCode={setCode}
                onClickResend={handleResend}
                method={method}
              />
            </div>
          ) : (
            <div className="change-credentials__form__step" key="step-password">
              <div className="change-credentials__form__step__password-section">
                <AdvancedInput
                  label={translate(
                    'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.PASSWORD.LABEL'
                  )}
                  onChange={handleChangePassword}
                  value={password}
                  securedTextField={!showPassword}
                  endIcon={
                    !showPassword ? (
                      <EyeOutlined onClick={() => setShowPassword(true)} />
                    ) : (
                      <EyeInvisibleOutlined
                        onClick={() => setShowPassword(false)}
                      />
                    )
                  }
                  maxLength={passwordRules.maxLength}
                />
                <div className="mt-1">
                  <PasswordCheck
                    password={password}
                    passwordValidation={validatePassword(password)}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="change-credentials__form__actions">
            {isChannelSelected ? (
              <AdvancedButton
                disabled={disableButton}
                fullWidth
                text={translate(
                  `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.${
                    isSmsFocused ? 'CODE' : 'PASSWORD'
                  }.OK`
                )}
                onClick={handleSubmit}
              />
            ) : null}
            <AdvancedButton
              variant="outlined"
              fullWidth
              text={translate(
                !isChannelSelected
                  ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CHANNEL.CANCEL'
                  : `PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.${
                      isSmsFocused ? 'CODE' : 'PASSWORD'
                    }.CANCEL`
              )}
              onClick={handleLogout}
            />
          </div>
        </div>
      </div>
      <MessageModal
        title={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.MODAL.TITLE')}
        message={translate(
          'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.MODAL.MESSAGE'
        )}
        buttonOkText={translate(
          'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.MODAL.OK'
        )}
        onConfirm={() => setIsChangeCredentialMessageModalVisible(false)}
        setVisible={setIsChangeCredentialMessageModalVisible}
        visible={isChangeCredentialMessageModalVisible}
      />
    </>
  );
};

export default ChangePassword;
